import { number, z, ZodSchema, type ZodErrorMap } from 'zod';
import { generateUUID, repeat, unreachable } from '../util';
import { TeamKleurenDataSchema, TeamKleurenMessageSchema, TeamKleurenUpdateSchema, type TeamKleurenState } from './team-kleuren';
import {
	KwaliteitenJachtDataSchema,
	KwaliteitenJachtMessageSchema,
	KwaliteitenJachtUpdateSchema,
	type KwaliteitenJachtState,
} from './kwaliteiten-jacht';
import { ZintuigenDataSchema, ZintuigenMessageSchema, ZintuigenUpdateSchema, type ZintuigenState } from './facilitaire-zintuigen';
import { JackpotDataSchema, JackpotMessageSchema, JackpotUpdateSchema, type JackpotState } from './jackpot';
import {
	VingerAfdrukkenDataSchema,
	VingerAfdrukkenMessageSchema,
	VingerAfdrukkenUpdateSchema,
	type VingerAfdrukkenState,
} from './vinger-afdrukken';
import { ReflectoraDataSchema, ReflectoraMessageSchema, ReflectoraUpdateSchema, type ReflectoraState } from './reflectora';
import {
	BouwHetLandschapDataSchema,
	BouwHetLandschapMessageSchema,
	BouwHetLandschapUpdateSchema,
	type BouwHetLandschapState,
} from './bouw-het-landschap';
import { FlikkenDataSchema, FlikkenMessageSchema, FlikkenUpdateSchema, type FlikkenState } from './flikken-challenge';
import {
	PlayAndConnectDataSchema,
	PlayAndConnectMessageSchema,
	PlayAndConnectPresetSchema,
	PlayAndConnectUpdateSchema,
	type PlayAndConnectPreset,
	type PlayAndConnectState,
} from './play-and-connect';

const customErrorMap: ZodErrorMap = (issue, ctx) => {
	// console.log('got issue', issue);

	if (issue.code === 'invalid_string') return { message: `validation.invalid_${issue.validation}` };

	return { message: ctx.defaultError };
};
z.setErrorMap(customErrorMap);

export const NonEmptyStringSchema = z.string().min(1);
export const EmailSchema = z.string().email();
export const PasswordSchema = z
	.string() //
	.min(8)
	.max(100)
	.regex(/[A-Z]/, 'validation.at_least_1_uppercase_letter')
	.regex(/[a-z]/, 'validation.at_least_1_lowercase_letter')
	.regex(/[0-9]/, 'validation.at_least_1_digit')
	.regex(/[\W_]/, 'validation.at_least_1_special_character');

const DomainSchema = z.string().regex(/^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/, 'validation.invalid_domain');

export const JwtSchema = z.object({
	userId: z.string(),
});

export const ModelSchema = z.object({
	_key: z.string().optional(),
	_id: z.string().optional(),
	_rev: z.string().optional(),
	_deleted: z.boolean().optional(),
	_log: z
		.object({
			createdOn: z.number().optional(),
			createdBy: z.string().optional(),
			changedOn: z.number().optional(),
			changedBy: z.string().optional(),
		})
		.optional(),
});
export type Model = z.infer<typeof ModelSchema>;

export const gameIds = [
	'team-kleuren-spel',
	'kwaliteiten-jacht',
	'facilitaire-zintuigen-spel',
	'jackpot-van-las-vegas',
	'vinger-afdrukken-spel',
	'bouw-het-landschap-spel',
	'reflectora-spel',
	'flikken-challenge',
	'play-and-connect-spel',
] as const;
export const GameIdSchema = z.enum(gameIds);
export type GameId = (typeof gameIds)[number];

const gameDataSchemas = {
	'team-kleuren-spel': TeamKleurenDataSchema,
	'kwaliteiten-jacht': KwaliteitenJachtDataSchema,
	'facilitaire-zintuigen-spel': ZintuigenDataSchema,
	'jackpot-van-las-vegas': JackpotDataSchema,
	'vinger-afdrukken-spel': VingerAfdrukkenDataSchema,
	'bouw-het-landschap-spel': BouwHetLandschapDataSchema,
	'flikken-challenge': FlikkenDataSchema,
	'reflectora-spel': ReflectoraDataSchema,
	'play-and-connect-spel': PlayAndConnectDataSchema,
} as const;
export type GameData<T extends keyof typeof gameDataSchemas> = z.infer<(typeof gameDataSchemas)[T]>;

export type IStage = 'starting' | 'register-players' | 'play-rounds' | 'ending-report';

export type IGameState = {
	stage: IStage;
	language: Language;
};

export type GameState = {
	'team-kleuren-spel': TeamKleurenState;
	'kwaliteiten-jacht': KwaliteitenJachtState;
	'facilitaire-zintuigen-spel': ZintuigenState;
	'jackpot-van-las-vegas': JackpotState;
	'vinger-afdrukken-spel': VingerAfdrukkenState;
	'bouw-het-landschap-spel': BouwHetLandschapState;
	'flikken-challenge': FlikkenState;
	'reflectora-spel': ReflectoraState;
	'play-and-connect-spel': PlayAndConnectState;
};

export const gameUpdateSchemas = {
	'team-kleuren-spel': TeamKleurenUpdateSchema,
	'kwaliteiten-jacht': KwaliteitenJachtUpdateSchema,
	'facilitaire-zintuigen-spel': ZintuigenUpdateSchema,
	'jackpot-van-las-vegas': JackpotUpdateSchema,
	'vinger-afdrukken-spel': VingerAfdrukkenUpdateSchema,
	'bouw-het-landschap-spel': BouwHetLandschapUpdateSchema,
	'flikken-challenge': FlikkenUpdateSchema,
	'reflectora-spel': ReflectoraUpdateSchema,
	'play-and-connect-spel': PlayAndConnectUpdateSchema,
} as const;
export type GameUpdates<T extends keyof typeof gameUpdateSchemas> = z.infer<(typeof gameUpdateSchemas)[T]>;

const gameMessageSchemas = {
	'team-kleuren-spel': TeamKleurenMessageSchema,
	'kwaliteiten-jacht': KwaliteitenJachtMessageSchema,
	'facilitaire-zintuigen-spel': ZintuigenMessageSchema,
	'jackpot-van-las-vegas': JackpotMessageSchema,
	'vinger-afdrukken-spel': VingerAfdrukkenMessageSchema,
	'bouw-het-landschap-spel': BouwHetLandschapMessageSchema,
	'flikken-challenge': FlikkenMessageSchema,
	'reflectora-spel': ReflectoraMessageSchema,
	'play-and-connect-spel': PlayAndConnectMessageSchema,
} as const;
export type GameMessages<T extends keyof typeof gameMessageSchemas> = z.infer<(typeof gameMessageSchemas)[T]>;

function defGame<const T extends GameId, const U extends ZodSchema>(id: T, data: U) {
	return z.object({ kind: z.literal(id), data });
}

export const languages = ['nl', 'en'] as const;
export type Languages = (typeof languages)[number];
export function languageUsageForGame(gameId: GameId): Languages[] {
	// prettier-ignore
	switch (gameId) {
		case 'team-kleuren-spel': return ['nl'];
		case 'kwaliteiten-jacht': return ['nl'];
		case 'facilitaire-zintuigen-spel': return ['nl'];
		case 'jackpot-van-las-vegas': return ['nl'];
		case 'vinger-afdrukken-spel': return ['nl'];
		case 'bouw-het-landschap-spel': return ['nl'];
		case 'flikken-challenge': return ['nl'];
		case 'reflectora-spel': return ['nl', 'en'];
		case 'play-and-connect-spel': return ['nl'];
		default: unreachable(gameId);
	}
}
export const LanguageSchema = z.enum(languages);
export type Language = z.infer<typeof LanguageSchema>;
export const languagesOptions: { value: Languages; label: string }[] = [
	{ value: 'nl', label: 'NL' },
	{ value: 'en', label: 'EN' },
];

const presetUsageKinds = ['no-preset', 'required-preset', 'optional-preset'] as const;
export type PresetUsage = (typeof presetUsageKinds)[number];
export function presetUsageForGame(gameId: GameId): PresetUsage {
	// prettier-ignore
	switch (gameId) {
		case 'team-kleuren-spel': return 'no-preset';
		case 'kwaliteiten-jacht': return 'no-preset';
		case 'facilitaire-zintuigen-spel': return 'required-preset';
		case 'jackpot-van-las-vegas': return 'required-preset'
		case 'vinger-afdrukken-spel': return 'required-preset'
		case 'bouw-het-landschap-spel': return 'required-preset';
		case 'flikken-challenge': return 'no-preset'
		case 'reflectora-spel': return 'no-preset'
		case 'play-and-connect-spel': return 'required-preset'
		default: unreachable(gameId);
	}
}

// Games and their data as defined by the game designer.
export const GameSchema = ModelSchema.and(
	z.object({
		// shared fields
		name: z.string().optional(),
	}),
).and(
	z.discriminatedUnion('kind', [
		defGame('team-kleuren-spel', gameDataSchemas['team-kleuren-spel']),
		defGame('kwaliteiten-jacht', gameDataSchemas['kwaliteiten-jacht']),
		defGame('facilitaire-zintuigen-spel', gameDataSchemas['facilitaire-zintuigen-spel']),
		defGame('jackpot-van-las-vegas', gameDataSchemas['jackpot-van-las-vegas']),
		defGame('vinger-afdrukken-spel', gameDataSchemas['vinger-afdrukken-spel']),
		defGame('bouw-het-landschap-spel', gameDataSchemas['bouw-het-landschap-spel']),
		defGame('flikken-challenge', gameDataSchemas['flikken-challenge']),
		defGame('reflectora-spel', gameDataSchemas['reflectora-spel']),
		defGame('play-and-connect-spel', gameDataSchemas['play-and-connect-spel']),
	]),
);
export type Game = z.infer<typeof GameSchema>;

export const PlaySessionSchema = z.object({
	uuid: z.string().default(generateUUID),
	from: z.string().datetime(),
	to: z.string().datetime().optional(),
	data: z.any(),
	language: LanguageSchema.default('nl'),
	archivedAt: z.string().datetime().optional(),
});
export type PlaySession = z.infer<typeof PlaySessionSchema>;

// Licenses given to retailers to play a given game
export const GameLicenseSchema = ModelSchema.and(
	z.object({
		kind: GameIdSchema,
		userId: z.string(),
		name: z.string().optional(),
		code: z
			.string()
			.optional()
			.transform((val) => val?.toUpperCase()),
		playSessions: z.array(PlaySessionSchema).default([]),
		presets: z.array(z.any()).default([]),
	}),
);
export type GameLicense = z.infer<typeof GameLicenseSchema>;

export const GameLicenseUpdateSchema = z.object({
	_id: z.string(),
	userId: z.string().optional(),
	name: z.string().optional(),
	code: z.string().optional(),
	presets: z.array(z.any()).optional(),
});
export type GameLicenseUpdate = z.infer<typeof GameLicenseUpdateSchema>;

export const UserRole = z.enum(['admin', 'game-master']);
export type UserRole = z.infer<typeof UserRole>;

export function displayRole(role: z.infer<typeof UserRole>): string {
	// prettier-ignore
	switch (role) {
		case 'admin': return 'Admin';
		case 'game-master': return 'Game Master';
		// case 'guest': return 'Guest';
		default: unreachable(role);
	}
}

export const UserSchema = ModelSchema.and(
	z.object({
		firstName: z.string(),
		lastName: z.string(),
		email: z.string(),
		role: UserRole.default('game-master'),
		password: z.string().optional(),
	}),
);
export type User = z.infer<typeof UserSchema>;

export const UserCreateSchema = z.object({
	firstName: NonEmptyStringSchema,
	lastName: NonEmptyStringSchema,
	email: EmailSchema,
	role: UserRole,
	password: PasswordSchema,
});
export type UserCreate = z.infer<typeof UserCreateSchema>;

export const UserUpdateSchema = z.object({
	_id: NonEmptyStringSchema,
	firstName: NonEmptyStringSchema,
	lastName: NonEmptyStringSchema,
	email: EmailSchema,
	role: UserRole,
	password: PasswordSchema.optional(),
});
export type UserUpdate = z.infer<typeof UserUpdateSchema>;

export type Telemetry = Model & {
	player: string;
	event: string;
	key: string;
	value?: string;
};

export const LoginSchema = z.object({
	email: EmailSchema,
	password: NonEmptyStringSchema,
});
export type Login = z.infer<typeof LoginSchema>;

export { FileSchema, type FileType } from './common';
